/**
 * Dashboard History tab: past appointments, filters, search, rate (BooksyAppExpo-style).
 */
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  FaCalendar,
  FaSearch,
  FaTimes,
  FaStore,
  FaClipboardList,
  FaMoneyBillAlt,
  FaStickyNote,
  FaCheckCircle,
  FaClock,
} from 'react-icons/fa';
import { http } from '../../lib/http';
import DateIcon from '../DateIcon';
import { useI18n } from '../../contexts/I18n';
import { Card, TabLoadingBuffer } from './Card';
import AuthRequiredPanel, { isUnauthorizedError } from './AuthRequiredPanel';
import { formatDate, cleanText } from './utils';

const HistoryTab = memo(function HistoryTab({ history, busyHistory, errHistory, onRate, focusBookingId }) {
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState(t('dashFilterAll'));
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [shopImages, setShopImages] = useState({});
  const [shopImagesLoaded, setShopImagesLoaded] = useState({});
  const [selectedAppointmentDetails, setSelectedAppointmentDetails] = useState(null);

  useEffect(() => {
    if (selectedAppointmentDetails) {
      const contentArea = document.querySelector('.content-area');
      if (contentArea) contentArea.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }, [selectedAppointmentDetails]);

  useEffect(() => {
    if (!focusBookingId || !Array.isArray(history) || !history.length) return;
    const found = history.find((item) => String(item._id) === String(focusBookingId));
    if (found) setSelectedAppointmentDetails(found);
  }, [focusBookingId, history]);

  const normalizeImageUrl = useCallback((u) => {
    if (!u) return null;
    const raw = typeof u === 'string' ? u : (u?.url || u?.src || u?.path || '');
    if (!raw) return null;
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    let s = String(raw).replace(/\\/g, '/').replace(/^\.?\/*/, '');
    if (s.startsWith('uploads/')) return `${window.location.origin}/${s}`;
    if (s.startsWith('/uploads/')) return `${window.location.origin}${s}`;
    if (s.startsWith('/')) return `${window.location.origin}${s}`;
    return `${window.location.origin}/${s}`;
  }, []);

  useEffect(() => {
    const fetchShopImages = async () => {
      try {
        const { data } = await http.getJson('/businesses?limit=200&page=1').catch(() => ({ data: [] }));
        const shopsData = Array.isArray(data) ? data : [];
        const imagesMap = {};
        const loadedMap = {};
        const preloadPromises = history.map(appointment => {
          return new Promise((resolve) => {
            const shopName = appointment.shopName || '';
            const provider = appointment.provider || appointment.shopId;
            if (shopName || provider) {
              const found = shopsData.find(
                (s) =>
                  (shopName && (s.name || '').trim().toLowerCase() === shopName.trim().toLowerCase()) ||
                  (provider && String(s._id) === String(provider))
              );
              if (found) {
                const imageUrl = normalizeImageUrl(found.imageUrl || found.image);
                const finalUrl = imageUrl || '/images/shop-placeholder.png';
                imagesMap[appointment._id] = finalUrl;
                if (finalUrl !== '/images/shop-placeholder.png') {
                  const img = new Image();
                  img.onload = () => { loadedMap[appointment._id] = true; resolve(); };
                  img.onerror = () => { imagesMap[appointment._id] = '/images/shop-placeholder.png'; loadedMap[appointment._id] = true; resolve(); };
                  img.src = finalUrl;
                } else { loadedMap[appointment._id] = true; resolve(); }
              } else { imagesMap[appointment._id] = '/images/shop-placeholder.png'; loadedMap[appointment._id] = true; resolve(); }
            } else { imagesMap[appointment._id] = '/images/shop-placeholder.png'; loadedMap[appointment._id] = true; resolve(); }
          });
        });
        await Promise.all(preloadPromises);
        setShopImages(imagesMap);
        setShopImagesLoaded(loadedMap);
      } catch (err) {
        const placeholderMap = {};
        history.forEach(appointment => { placeholderMap[appointment._id] = '/images/shop-placeholder.png'; });
        setShopImages(placeholderMap);
      }
    };
    if (history.length > 0) fetchShopImages();
  }, [history, normalizeImageUrl]);

  const options = useMemo(
    () => [t('dashFilterAll'), ...new Set(history.map(a => a.status))],
    [history, t]
  );

  const filteredAndSortedRows = useMemo(() => {
    let filtered = filter === t('dashFilterAll') ? history : history.filter(a => a.status === filter);
    if (searchQuery) {
      filtered = filtered.filter(a =>
        a.shopName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.serviceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.staffName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.selectedService?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'date': aVal = new Date(a.selectedDate); bVal = new Date(b.selectedDate); break;
        case 'price': aVal = parseFloat(a.totalPrice) || 0; bVal = parseFloat(b.totalPrice) || 0; break;
        case 'shop': aVal = a.shopName || ''; bVal = b.shopName || ''; break;
        default: aVal = a.selectedDate; bVal = b.selectedDate;
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    return filtered;
  }, [filter, searchQuery, sortBy, sortOrder, history, t]);

  const stats = useMemo(() => {
    const total = history.length;
    const completed = history.filter(a => a.status === 'completed').length;
    const pending = history.filter(a => a.status === 'pending').length;
    const totalSpent = history.filter(a => a.status === 'completed').reduce((sum, a) => sum + (parseFloat(a.totalPrice) || 0), 0);
    return { total, completed, pending, totalSpent };
  }, [history]);

  if (busyHistory) return <TabLoadingBuffer label={t('dashLoading') || 'Loading...'} />;

  return (
    <>
      <Card extraClass="history-card">
        <div className={`history-header ${lang === 'ar' ? 'rtl' : ''}`}>
          <div className={`history-header-content ${lang === 'ar' ? 'rtl' : ''}`}>
            <h2 className={`history-title-row ${lang === 'ar' ? 'rtl' : ''}`}>
              <span className="tab-title-icon-wrap" aria-hidden="true">
                <FaCalendar className="history-title-icon" />
              </span>
              {t('dashHistoryTitle')}
            </h2>
            <p className="history-subtitle">{t('dashHistorySub')}</p>
          </div>
          <div className="history-stats">
            <div className="stat-item stat-item-total">
              <div className="stat-icon-wrap" aria-hidden="true"><FaCalendar size={16} /></div>
              <span className="stat-value">{cleanText(stats.total)}</span>
              <span className="stat-label">{t('dashStatTotal')}</span>
            </div>
            <div className="stat-item stat-item-completed">
              <div className="stat-icon-wrap" aria-hidden="true"><FaCheckCircle size={16} /></div>
              <span className="stat-value">{cleanText(stats.completed)}</span>
              <span className="stat-label">{t('dashStatCompleted')}</span>
            </div>
            <div className="stat-item stat-item-pending">
              <div className="stat-icon-wrap" aria-hidden="true"><FaClock size={16} /></div>
              <span className="stat-value">{cleanText(stats.pending)}</span>
              <span className="stat-label">{t('dashStatPending')}</span>
            </div>
            <div className="stat-item stat-item-total-spent">
              <div className="stat-icon-wrap" aria-hidden="true"><FaMoneyBillAlt size={16} /></div>
              <span className="stat-value">{cleanText(`${stats.totalSpent.toLocaleString('en-US')} EGP`)}</span>
              <span className="stat-label">{t('dashStatTotalSpent')}</span>
            </div>
          </div>
        </div>

        <div className="history-filters">
          <div className="history-filters-top-row">
            <div className="search-box">
              <FaSearch className="search-icon" size={18} color="#18442A" />
              <input type="text" placeholder={t('dashSearchAppointments')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
            </div>
            <button className="sort-toggle" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>{sortOrder === 'asc' ? '↑' : '↓'}</button>
          </div>
          <div className="history-filters-bottom-row">
            <select value={filter} onChange={e => setFilter(e.target.value)} className="filter-select">{options.map(o => <option key={o}>{o}</option>)}</select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
              <option value="date">{t('dashSortByDate')}</option>
              <option value="price">{t('dashSortByPrice')}</option>
              <option value="shop">{t('dashSortByShop')}</option>
            </select>
          </div>
        </div>

        {busyHistory ? (
          <div className="loading-state"><div className="loading-spinner"></div></div>
        ) : isUnauthorizedError(errHistory) ? (
          <AuthRequiredPanel hint={t('dashSessionExpiredHistoryHint')} />
        ) : errHistory ? (
          <div className="error-state"><p>{t('dashErrorPrefix')} {errHistory}</p></div>
        ) : filteredAndSortedRows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><DateIcon /></div>
            <h3>{t('dashNoneFound')}</h3>
            <p>{searchQuery ? t('dashTryAdjust') : t('dashNoneYet')}</p>
            {!searchQuery && <button className="btn primary" onClick={() => window.location.href = '/explore'}>{t('dashBookFirst')}</button>}
          </div>
        ) : (
          <div className="appointments-grid">
            {filteredAndSortedRows.map(a => {
              const statusColors = { pending: '#b45309', accepted: '#18442A', completed: '#047857', cancelled: '#b91c1c', declined: '#b91c1c' };
              const statusColor = statusColors[a.status] || '#667085';
              const dateTimeDisplay = `${formatDate(a.selectedDate)} ${cleanText(a.selectedHour)}`;
              const shopImage = shopImages[a._id] || '/images/shop-placeholder.png';
              const isImageLoaded = shopImagesLoaded[a._id];
              const isPlaceholder = shopImage === '/images/shop-placeholder.png';
              const showRealImage = isImageLoaded && !isPlaceholder;
              return (
                <div key={a._id} className="appointment-card">
                  <div className="appointment-card-top-row">
                    <div className="appointment-status-pill" style={{ backgroundColor: statusColor + '20', borderColor: statusColor, color: statusColor }}>
                      {a.status === 'completed' ? 'Completed' : a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                    </div>
                    <span className="appointment-date-time">{dateTimeDisplay}</span>
                  </div>
                  <div className="appointment-card-main-row">
                    <div className="appointment-shop-info">
                      <div className="appointment-shop-image-name-row">
                        {showRealImage ? (
                          <img src={shopImage} alt={a.shopName || 'Shop'} className="appointment-shop-logo" loading="eager" onError={(e) => { e.target.src = '/images/shop-placeholder.png'; }} />
                        ) : (
                          <div className="shop-image-loading-buffer" aria-hidden="true" />
                        )}
                        <div className="appointment-shop-details"><h4 className="appointment-shop-name">{a.shopName || 'Shop'}</h4></div>
                      </div>
                      <div className="appointment-shop-meta"><span className="appointment-order-id">Booking ID: {a._id?.slice(-10) || 'N/A'}</span></div>
                    </div>
                    <div className="appointment-order-summary">
                      <button className="appointment-view-details" onClick={(e) => { e.stopPropagation(); setSelectedAppointmentDetails(a); }}>View details</button>
                      <div className="appointment-order-price">{cleanText(`${Number(a.totalPrice||0).toLocaleString('en-US')} EGP`)}</div>
                      <div className="appointment-items-count"><span>1 service</span></div>
                    </div>
                  </div>
                  <div className="appointment-card-actions-row">
                    <div className="appointment-button-container">
                      {a.status === 'completed' && (
                        <div className="appointment-rating-stars">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const isFilled = a.reviewed && a.rating >= star;
                            return (
                              <button key={star} type="button" className={`appointment-star ${isFilled ? 'filled' : ''} ${a.reviewed ? 'disabled' : ''}`}
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (!a.reviewed && onRate) onRate(a, star); }} disabled={a.reviewed}
                                aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                                style={{ background: 'none', border: 'none', cursor: a.reviewed ? 'default' : 'pointer', padding: '4px', fontSize: '24px', minWidth: '32px', minHeight: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1' }}
                              >{isFilled ? '★' : '☆'}</button>
                            );
                          })}
                        </div>
                      )}
                      <button
                        type="button"
                        className="appointment-order-again-button"
                        onClick={() => window.location.href = `/booking/${encodeURIComponent(a.shopName || '')}`}
                      >
                        Book again
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {selectedAppointmentDetails && (
        <div className="appointment-details-backdrop" role="dialog" aria-modal="true" aria-labelledby="appointment-details-modal-title" onClick={() => setSelectedAppointmentDetails(null)}>
          <div className="appointment-details-modal" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const a = selectedAppointmentDetails;
              const statusStyle = { pending: { bg: '#b4530922', border: '#b45309', color: '#b45309' }, accepted: { bg: '#18442A22', border: '#18442A', color: '#18442A' }, completed: { bg: '#04785722', border: '#047857', color: '#047857' }, cancelled: { bg: '#b91c1c22', border: '#b91c1c', color: '#b91c1c' }, declined: { bg: '#b91c1c22', border: '#b91c1c', color: '#b91c1c' } };
              const style = statusStyle[a.status] || { bg: '#64748b22', border: '#64748b', color: '#64748b' };
              const statusLabel = a.status === 'completed' ? (t('dashCompleted') || 'Completed') : (a.status ? String(a.status).charAt(0).toUpperCase() + String(a.status).slice(1) : 'N/A');
              return (
                <>
                  <header className="appointment-details-header">
                    <div className="appointment-details-header-inner">
                      <h2 id="appointment-details-modal-title" className="appointment-details-title">{t('dashBookingDetails') || 'Booking Details'}</h2>
                      <span className="appointment-details-status-pill" style={{ backgroundColor: style.bg, borderColor: style.border, color: style.color }}>{statusLabel}</span>
                    </div>
                    <button type="button" className="appointment-details-close" onClick={() => setSelectedAppointmentDetails(null)} aria-label={t('close') || 'Close'}><FaTimes size={20} /></button>
                  </header>
                  <div className={`appointment-details-body ${lang === 'ar' ? 'rtl' : ''}`}>
                    <section className="appointment-details-card">
                      <h3 className="appointment-details-card-title"><FaStore className="appointment-details-card-icon" aria-hidden />{t('dashShopInfo') || 'Shop & booking'}</h3>
                      <div className="appointment-details-grid">
                        <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashShopName') || 'Shop'}</span><span className="appointment-details-item-value">{a.shopName || a.shop?.name || 'N/A'}</span></div>
                        <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashBookingId') || 'Booking ID'}</span><span className="appointment-details-item-value appointment-details-id">{a._id?.slice(-10) || 'N/A'}</span></div>
                      </div>
                    </section>
                    <section className="appointment-details-card">
                      <h3 className="appointment-details-card-title"><FaClipboardList className="appointment-details-card-icon" aria-hidden />{t('dashServiceInfo') || 'Service'}</h3>
                      <div className="appointment-details-grid">
                        <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashService') || 'Service'}</span><span className="appointment-details-item-value">{a.serviceName || a.service?.name || 'N/A'}</span></div>
                        {(a.staffName || a.staff?.name) && <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashStaff') || 'Staff'}</span><span className="appointment-details-item-value">{a.staffName || a.staff?.name || 'N/A'}</span></div>}
                      </div>
                    </section>
                    <section className="appointment-details-card">
                      <h3 className="appointment-details-card-title"><FaCalendar className="appointment-details-card-icon" aria-hidden />{t('dashDateAndTime') || 'Date & time'}</h3>
                      <div className="appointment-details-grid">
                        <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashDate') || 'Date'}</span><span className="appointment-details-item-value">{formatDate(a.selectedDate || a.date || a.appointmentTime) || 'N/A'}</span></div>
                        <div className="appointment-details-item"><span className="appointment-details-item-label">{t('dashTime') || 'Time'}</span><span className="appointment-details-item-value">{cleanText(a.selectedHour || a.time || a.appointmentTime) || 'N/A'}</span></div>
                      </div>
                    </section>
                    <section className="appointment-details-card appointment-details-card-highlight">
                      <h3 className="appointment-details-card-title"><FaMoneyBillAlt className="appointment-details-card-icon" aria-hidden />{t('dashPayment') || 'Payment'}</h3>
                      <div className="appointment-details-total">
                        <span className="appointment-details-total-label">{t('dashTotalPrice') || 'Total'}</span>
                        <span className="appointment-details-total-value">{cleanText(`${Number(a.totalPrice != null ? a.totalPrice : a.price || 0).toLocaleString('en-US')} EGP`)}</span>
                      </div>
                    </section>
                    {a.notes && (
                      <section className="appointment-details-card">
                        <h3 className="appointment-details-card-title"><FaStickyNote className="appointment-details-card-icon" aria-hidden />{t('dashNotes') || 'Notes'}</h3>
                        <p className="appointment-details-notes">{a.notes}</p>
                      </section>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
});

export default HistoryTab;
